/**
 * CreativeIterationOverlay — slide-up portal panel with three iteration
 * paths: A/B Variant, New Angle, Chat-Driven. Phase 3 wires the UI; the
 * actual skill invocation is Phase 4 (the spec defers direct skill calls).
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import type {
  CreativeBriefSummary,
  CreativeSiblingSummary,
  IterationPath,
  PDOFitConcept,
  WorkspaceBriefAngle,
} from './types';

interface CreativeIdentity {
  creative_id: string;
  form?: { headline?: string };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  initialPath?: IterationPath;
  preselectedModIndex?: number;
  creative: CreativeIdentity;
  brief: CreativeBriefSummary | null;
  rankedMods: Record<string, unknown> | null;
  siblings: CreativeSiblingSummary[];
  pdoSurpriseConcepts: PDOFitConcept[];
  latestReview: Record<string, unknown> | null;
}

interface BriefModSuggestion {
  recommendation?: { title?: string; rationale?: string } | null;
  rank_rationale?: string;
  delta?: { revenue_impact_delta_usd?: number | null } | null;
  [key: string]: unknown;
}

const PATH_TABS: Array<{ key: IterationPath; label: string; desc: string }> = [
  { key: 'ab_variant', label: 'A/B Variant', desc: 'Change one element' },
  { key: 'new_angle', label: 'New Angle', desc: 'Different strategy' },
  { key: 'chat_driven', label: 'Chat', desc: 'Describe it' },
];

export function CreativeIterationOverlay({
  isOpen,
  onClose,
  initialPath = 'ab_variant',
  preselectedModIndex,
  creative,
  brief,
  rankedMods,
  siblings: _siblings,
  pdoSurpriseConcepts,
  latestReview,
}: Props) {
  const [activePath, setActivePath] = useState<IterationPath>(initialPath);

  useEffect(() => {
    if (isOpen) setActivePath(initialPath);
  }, [isOpen, initialPath]);

  if (!isOpen) return null;

  const ranking = rankedMods as { suggestions?: BriefModSuggestion[] } | null;
  const suggestions = ranking?.suggestions ?? [];
  const briefAngles = (brief?.selected_angles as WorkspaceBriefAngle[] | undefined) ?? [];

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
        aria-label="Close iteration overlay"
      />

      <div
        className="fixed bottom-0 inset-x-0 z-50 bg-white rounded-t-2xl shadow-2xl border-t border-slate-200 flex flex-col"
        style={{ maxHeight: '65vh' }}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>

        <div className="px-5 pb-3 flex items-center justify-between border-b border-slate-100">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
              Iterate on
            </p>
            <h3 className="text-sm font-bold text-navy-900 truncate max-w-[400px]">
              {creative.form?.headline ?? creative.creative_id}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex gap-0 border-b border-slate-100">
          {PATH_TABS.map(({ key, label, desc }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActivePath(key)}
              className={clsx(
                'flex-1 px-4 py-2.5 text-left transition-colors',
                activePath === key
                  ? 'bg-teal-50 border-b-2 border-teal-500 text-teal-700'
                  : 'text-slate-500 hover:bg-slate-50',
              )}
            >
              <p
                className={clsx(
                  'text-xs font-semibold',
                  activePath === key ? 'text-teal-700' : 'text-slate-700',
                )}
              >
                {label}
              </p>
              <p className="text-[10px] font-mono text-slate-400">{desc}</p>
            </button>
          ))}
        </div>

        <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(65vh - 160px)' }}>
          {activePath === 'ab_variant' && (
            <ABVariantPath
              suggestions={suggestions}
              preselectedModIndex={preselectedModIndex}
              onSubmit={() => onClose()}
            />
          )}
          {activePath === 'new_angle' && (
            <NewAnglePath
              briefAngles={briefAngles}
              pdoSurpriseConcepts={pdoSurpriseConcepts}
              onSelectAngle={() => onClose()}
              onSuggestAngles={() => onClose()}
            />
          )}
          {activePath === 'chat_driven' && (
            <ChatDrivenPath
              creative={creative}
              latestReview={latestReview}
              topModRationale={
                (suggestions[0]?.rank_rationale as string | undefined) ??
                (suggestions[0]?.recommendation?.rationale as string | undefined) ??
                ''
              }
            />
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

// =============================================================================
// Path implementations
// =============================================================================

function ABVariantPath({
  suggestions,
  preselectedModIndex,
  onSubmit,
}: {
  suggestions: BriefModSuggestion[];
  preselectedModIndex?: number;
  onSubmit: (description: string) => void;
}) {
  const [selectedIdx, setSelectedIdx] = useState<number>(preselectedModIndex ?? 0);
  const [customDescription, setCustomDescription] = useState('');

  useEffect(() => {
    if (preselectedModIndex != null) setSelectedIdx(preselectedModIndex);
  }, [preselectedModIndex]);

  const top4 = suggestions.slice(0, 4);
  const selected = top4[selectedIdx] as BriefModSuggestion | undefined;

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-600">
        Select a modification to test, or describe a custom change. The modification will be
        generated as a sibling creative from the same brief and angle.
      </p>

      {top4.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase text-slate-400">PDO-Ranked Suggestions</p>
          {top4.map((mod, idx) => {
            const delta = mod.delta?.revenue_impact_delta_usd;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => setSelectedIdx(idx)}
                className={clsx(
                  'w-full text-left rounded-xl border p-3 transition-colors',
                  selectedIdx === idx
                    ? 'border-teal-400 bg-teal-50'
                    : 'border-slate-200 hover:border-teal-200',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-semibold text-navy-900">
                    {mod.recommendation?.title ?? 'Modification'}
                  </p>
                  {delta != null && (
                    <span className="text-[10px] font-mono text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5 flex-shrink-0">
                      +${delta.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">{mod.rank_rationale ?? ''}</p>
              </button>
            );
          })}
        </div>
      )}

      <div className="space-y-1.5">
        <p className="text-[10px] font-mono uppercase text-slate-400">Or Describe It</p>
        <textarea
          value={customDescription}
          onChange={(e) => setCustomDescription(e.target.value)}
          placeholder='e.g. "Change the visual to show the product in a kitchen scene with warm lighting"'
          rows={3}
          className="w-full text-xs border border-slate-200 rounded-xl p-3 resize-none focus:outline-none focus:border-teal-400"
        />
      </div>

      <button
        type="button"
        onClick={() =>
          onSubmit(customDescription || (selected?.rank_rationale as string | undefined) || '')
        }
        className="w-full bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold rounded-xl py-2.5 transition-colors"
      >
        Generate Variant →
      </button>
    </div>
  );
}

function NewAnglePath({
  briefAngles,
  pdoSurpriseConcepts,
  onSelectAngle,
  onSuggestAngles,
}: {
  briefAngles: WorkspaceBriefAngle[];
  pdoSurpriseConcepts: PDOFitConcept[];
  onSelectAngle: (angleId: string) => void;
  onSuggestAngles: () => void;
}) {
  const approvedAngles = briefAngles.filter(
    (a) => a.status === 'approved' || a.status === 'suggested',
  );
  return (
    <div className="space-y-4">
      {approvedAngles.length > 0 ? (
        <>
          <p className="text-xs text-slate-600">
            Generate a creative from a different approved angle in the linked brief.
          </p>
          <div className="space-y-2">
            {approvedAngles.map((angle) => (
              <button
                key={angle.angle_id}
                type="button"
                onClick={() => onSelectAngle(angle.angle_id)}
                className="w-full text-left rounded-xl border border-slate-200 hover:border-teal-300 p-3 transition-colors"
              >
                <p className="text-xs font-semibold text-navy-900">
                  {angle.headline ?? angle.hook ?? angle.message ?? angle.angle_id}
                </p>
                {(angle.scenario ?? angle.scenario_name) && (
                  <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                    {angle.scenario ?? angle.scenario_name}
                  </p>
                )}
                {(angle.driving_pdo_factors ?? []).slice(0, 3).length > 0 && (
                  <div className="flex items-center gap-2 mt-1.5">
                    {(angle.driving_pdo_factors ?? []).slice(0, 3).map((f) => (
                      <span
                        key={f.concept_id}
                        className="text-[9px] font-mono text-teal-600 bg-teal-50 border border-teal-100 rounded px-1.5 py-0.5"
                      >
                        {f.concept_name ?? f.concept_id}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      ) : (
        <p className="text-xs text-slate-500">No other approved angles in the linked brief.</p>
      )}

      {pdoSurpriseConcepts.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase text-slate-400">
            Or Build From Surprise Signals
          </p>
          <p className="text-xs text-slate-600">
            The review detected {pdoSurpriseConcepts.length} concept
            {pdoSurpriseConcepts.length > 1 ? 's' : ''} not in the brief:
            {pdoSurpriseConcepts
              .slice(0, 3)
              .map((c) => ` ${c.concept_name}`)
              .join(',')}
            {pdoSurpriseConcepts.length > 3 ? '...' : ''}.
            Ask the agent to suggest new angles based on these signals.
          </p>
          <button
            type="button"
            onClick={onSuggestAngles}
            className="w-full border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-xl py-2.5 transition-colors"
          >
            Suggest New Angles From Surprise Signals →
          </button>
        </div>
      )}

      {approvedAngles.length === 0 && pdoSurpriseConcepts.length === 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-slate-500">No angles or surprise signals available.</p>
          <p className="text-xs text-slate-400 mt-1">
            Add approved angles to the linked brief, or run a creative review to discover surprise
            signals.
          </p>
        </div>
      )}
    </div>
  );
}

function ChatDrivenPath({
  creative,
  latestReview,
  topModRationale,
}: {
  creative: CreativeIdentity;
  latestReview: Record<string, unknown> | null;
  topModRationale: string;
}) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const headline = creative.form?.headline ?? creative.creative_id;
  const formulaSummary = buildFormulaSummary(latestReview);
  const reviewSummary = buildReviewSummary(latestReview);
  const contextSummary = [
    `Creative: "${headline}"`,
    `Formula: ${formulaSummary}`,
    reviewSummary ? `Review: ${reviewSummary}` : null,
    topModRationale ? `Top recommendation: ${topModRationale}` : null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <div className="space-y-3">
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
        <p className="text-[10px] font-mono uppercase text-slate-400 mb-1">Context loaded</p>
        <pre className="text-[11px] text-slate-600 whitespace-pre-wrap leading-relaxed font-mono">
          {contextSummary}
        </pre>
      </div>

      <div className="space-y-1.5">
        <p className="text-[10px] font-mono uppercase text-slate-400">What I want to change</p>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          autoFocus
          placeholder='e.g. "Make the image brighter, use a kitchen scene, and make the headline more direct"'
          rows={4}
          className="w-full text-xs border border-slate-200 rounded-xl p-3 resize-none focus:outline-none focus:border-teal-400"
        />
      </div>

      <button
        type="button"
        disabled={!message.trim()}
        className="w-full bg-teal-500 hover:bg-teal-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl py-2.5 transition-colors"
      >
        Send to Agent →
      </button>
      <p className="text-[10px] font-mono text-slate-400 text-center">
        The agent will call modify_ad, generate_angles, or other tools as appropriate.
      </p>
    </div>
  );
}

function buildFormulaSummary(latestReview: Record<string, unknown> | null): string {
  if (!latestReview) return 'unknown';
  const tax = (latestReview.taxonomy_extraction as Record<string, unknown> | undefined) ?? {};
  const hook =
    ((tax.hook_analysis as Record<string, unknown> | undefined)?.hook_type as string) ?? '—';
  const angle =
    ((tax.angle_analysis as Record<string, unknown> | undefined)?.angle_archetype as string) ?? '—';
  const cta = ((tax.cta_analysis as Record<string, unknown> | undefined)?.cta_type as string) ?? '—';
  return `Hook: ${hook} · Angle: ${angle} · CTA: ${cta}`;
}

function buildReviewSummary(latestReview: Record<string, unknown> | null): string {
  if (!latestReview) return '';
  const verdict = (latestReview.verdict as string) ?? 'unknown';
  const score = latestReview.overall_score;
  const strength = (latestReview.top_strength as string) ?? '';
  return `Verdict: ${verdict}${score != null ? ` · Score: ${score}/10` : ''}${
    strength ? ` · Top strength: ${strength}` : ''
  }`;
}
